import { PrimaryButton, PrimaryButtonProps, useModal } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'

import { getCurrentQuote, isAddOn } from '../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../hooks/auth'
import { useDiscountCovers, useOptionalCovers } from '../../../hooks/covers'
import { useErrorHandlingAsyncCallback } from '../../../hooks/errors'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { usePolicyholder, usePrincipal } from '../../authentication'
import { usePaymentSchedule } from '../../product/paymentScheduleContext'
import { useProduct } from '../../product/productContext'
import { useProposal } from '../../product/proposal/proposalContext'
import { ProposalPath } from '../../product/proposal/ProposalPath'
import { IdCheckFailedModal } from './IdCheckFailedModal'

export interface ProposalContinueButtonProps extends PrimaryButtonProps {
  tKey: TKeyBuilder
  disabled?: boolean
}

export const ProposalContinueButton: FunctionComponent<ProposalContinueButtonProps> = ({
  tKey,
  disabled = false,
  ...props
}) => {
  const { t } = useWriskTranslation()
  const { apiClient } = usePrincipal()
  const modal = useModal()

  const navigate = useNavigate()

  const { proposal, setProposal } = useProposal()
  const { product } = useProduct()
  const { policyholder } = usePolicyholder()
  const { isPaymentScheduleDirty, getScheduleChanges } = usePaymentSchedule()

  const quote = getCurrentQuote(proposal)
  const covers = useOptionalCovers(quote, product)
    .filter(isAddOn(product))
    .filter((it) => it.available)

  const [discount] = useDiscountCovers(quote)

  const { execute: updateProposal } = useApiErrorHandlingAsyncCallback(async () => {
    if (isPaymentScheduleDirty) {
      setProposal(
        await apiClient.updateProposal(proposal.proposalCode, {
          applyRating: true,
          changes: getScheduleChanges(),
        }),
      )
    }
  })

  const onButtonClick = useErrorHandlingAsyncCallback(async () => {
    await updateProposal()
    const idCheckResponse = await apiClient.idCheck(proposal.proposalCode)

    idCheckResponse.isClear
      ? navigate(
          covers.length
            ? ProposalPath.ADD_ONS
            : policyholder?.accountRegistered
            ? ProposalPath.SUMMARY
            : ProposalPath.ACCOUNT_SETUP,
        )
      : modal.show({
          content: <IdCheckFailedModal />,
        })
  })

  const buttonKey = discount
    ? discount.active
      ? 'actions.active-promo'
      : 'actions.inactive-promo'
    : 'actions.continue'

  return (
    <PrimaryButton
      data-testid='proposal-continue'
      onClick={onButtonClick.execute}
      disabled={disabled}
      loading={onButtonClick.loading}
      {...props}
    >
      {t(tKey(buttonKey))}
    </PrimaryButton>
  )
}
